import { ReactNode, useState } from 'react';

import {
  Alert,
  HorizontalTabsList,
  Tabs,
  TabsContent,
  TabsTrigger,
  useToggle,
} from '@commenda/kakashi';
import { TaskParty } from '@prisma/client';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IndirectTaxFilingsHelper } from '../helpers';
import {
  IIndirectTaxFilingDetail,
  IIndirectTaxFilingReturnSummary,
} from '../types';
import { IndirectTaxFilingDetailActivity } from './IndirectTaxFilingDetailActivity';

import { IndirectTaxFilingDetailDocumentsList } from './IndirectTaxFilingDetailDocumentsList';
import { IndirectTaxFilingDetailInformation } from './IndirectTaxFilingDetailInformation';
import { IndirectTaxFilingDetailMetadataLine } from './IndirectTaxFilingDetailMetadataLine';
import { IndirectTaxFilingDetailMetrics } from './IndirectTaxFilingDetailMetrics';
import { IndirectTaxFilingDetailRemittanceAccount } from './IndirectTaxFilingDetailRemittanceAccount';
import { IndirectTaxFilingDetailReturnSummary } from './IndirectTaxFilingDetailReturnSummary';
import { IndirectTaxFilingDetailTaskBar } from './IndirectTaxFilingDetailTaskBar';
import { IndirectTaxFilingDetailVatReturnSummary } from './IndirectTaxFilingDetailVatReturnSummary';
import { IndirectTaxFilingViewTransactionsModal } from './IndirectTaxFilingViewTransactionsModal';
import { useIndirectTaxFilingTransactionCount } from './useIndirectTaxFilingTransactionCount';

export interface IndirectTaxFilingDetailPageProps {
  filing: IIndirectTaxFilingDetail;
  region: IndirectTaxRegion;
  returnSummary: IIndirectTaxFilingReturnSummary | null;
  corporationId: number;
  companyId: number;
  header: ReactNode;
  onFilingChanged?: () => void;
  showActivityTab?: boolean;
  // Party viewing this page. Drives the activity-tab task role and gates the
  // client-only task-bar actions (only the client can approve a filing; the
  // agent sees the step but the action is disabled).
  viewerRole?: TaskParty;
  showAgentReviewBanner?: boolean;
  showFilingInformationTab?: boolean;
  redirectToRegistrationPage?: (registrationId: string) => void;
}

const IndirectTaxFilingWorkflowDetailPage = ({
  filing,
  region,
  returnSummary,
  corporationId,
  companyId,
  header,
  onFilingChanged,
  showActivityTab = false,
  viewerRole = TaskParty.CLIENT,
  showAgentReviewBanner = false,
  showFilingInformationTab = false,
  redirectToRegistrationPage,
}: IndirectTaxFilingDetailPageProps) => {
  const filingDetailTabs = [
    { value: 'summary', label: 'Summary' },
    ...(showActivityTab ? [{ value: 'activity', label: 'Activity' }] : []),
    ...(showFilingInformationTab
      ? [{ value: 'information', label: 'Filing Information' }]
      : []),
  ];
  const { currency } = filing;
  const [activeTab, setActiveTab] = useState<string>('summary');
  const viewTransactionsModal = useToggle();

  // Live transaction count from the same query the "View transactions" modal
  // runs, so the metric and the modal always agree (the stored count is 0 for
  // contribution-driven filings).
  const { count: liveTransactionCount, isLoading: isLoadingTransactionCount } =
    useIndirectTaxFilingTransactionCount({ filing, region, corporationId });

  // VAT filings hide the return summary until the agent first submits the
  // filing report; other filings always show it. The check is monotonic so
  // the summary stays visible after a client "report issue" reopens step 1.
  const isContributionDriven =
    IndirectTaxRegionHelper.isContributionRegion(region);
  const hasGeneratedFilingReport =
    IndirectTaxFilingsHelper.hasGeneratedFilingReportAtLeastOnce(
      filing.serviceRequest
    );
  const isAwaitingReport =
    IndirectTaxRegionHelper.hidesAmountsUntilReportGenerated(region) &&
    !hasGeneratedFilingReport;
  const shouldShowReturnSummary = !isAwaitingReport;

  const amountDue = isAwaitingReport ? null : filing.amountDue;

  // Enterprise-only banner: while a contribution-driven filing's service request is still on its
  // first step (GENERATE_FILING_REPORT), the agent is reviewing and the client
  // task bar is hidden — surface an info banner so the tab isn't blank.
  const isAgentReviewingFiling =
    showAgentReviewBanner &&
    isContributionDriven &&
    IndirectTaxFilingsHelper.isOnGenerateFilingReportStep(
      filing.serviceRequest
    );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab}>
      <div className="border-b border-border-primary p-3xl pb-0">
        <div className="mb-md">{header}</div>
        <IndirectTaxFilingDetailMetadataLine filing={filing} />

        <HorizontalTabsList
          variant="underline"
          size="sm"
          className="border-b-0 mt-3xl gap-lg"
        >
          {filingDetailTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value}>
              {tab.label}
            </TabsTrigger>
          ))}
        </HorizontalTabsList>
      </div>

      <TabsContent value="summary">
        {isAgentReviewingFiling && (
          <div className="px-3xl pt-3xl">
            <Alert
              variant="info"
              title="Return under preparation"
              description="The Commenda team is reviewing your transactions and calculating the return summary, including any adjustments. The amounts shown may change until the filing is complete. No action is needed from you."
            />
          </div>
        )}

        <IndirectTaxFilingDetailTaskBar
          tasks={filing.tasks}
          filingId={filing.id}
          corporationId={corporationId}
          region={region}
          serviceRequest={filing.serviceRequest}
          onTaskCompleted={onFilingChanged}
          viewerRole={viewerRole}
        />

        <IndirectTaxFilingDetailMetrics
          amountDue={amountDue}
          currency={currency}
          transactionCount={
            isLoadingTransactionCount
              ? filing.transactionCount
              : liveTransactionCount
          }
          filedOn={filing.filedOn}
          onViewTransactions={viewTransactionsModal.show}
        />

        <div className="grid grid-cols-1 gap-6xl p-3xl md:grid-cols-2">
          {isContributionDriven ? (
            // Contribution regions: transaction-derived "Return summary" on top,
            // with the box breakdown in a separate "Return boxes" panel below.
            <div className="flex flex-col gap-6xl">
              {shouldShowReturnSummary && returnSummary ? (
                <IndirectTaxFilingDetailReturnSummary
                  summary={returnSummary}
                  title="Return boxes"
                />
              ) : (
                <IndirectTaxFilingDetailVatReturnSummary
                  filing={filing}
                  region={region}
                  corporationId={corporationId}
                />
              )}
            </div>
          ) : shouldShowReturnSummary && returnSummary ? (
            <IndirectTaxFilingDetailReturnSummary summary={returnSummary} />
          ) : null}
          <div className="flex flex-col gap-3xl">
            <div className="flex flex-col gap-xl">
              <span className="txt-medium-bold text-text-primary">
                Filing details
              </span>
              <div className="flex flex-col gap-xl">
                <IndirectTaxFilingDetailRemittanceAccount
                  account={filing.remittanceAccount}
                />
              </div>
            </div>
            <IndirectTaxFilingDetailDocumentsList
              filingConfirmationDocuments={filing.filingConfirmationDocuments}
              relatedDocuments={filing.relatedDocuments}
            />
          </div>
        </div>
      </TabsContent>

      {showActivityTab && (
        <TabsContent value="activity">
          <IndirectTaxFilingDetailActivity
            serviceRequest={filing.serviceRequest}
            role={viewerRole}
          />
        </TabsContent>
      )}

      {showFilingInformationTab && (
        <TabsContent value="information">
          <IndirectTaxFilingDetailInformation
            filing={filing}
            corporationId={corporationId}
            redirectToRegistrationPage={redirectToRegistrationPage}
          />
        </TabsContent>
      )}

      <IndirectTaxFilingViewTransactionsModal
        isOpen={viewTransactionsModal.visible}
        onClose={viewTransactionsModal.hide}
        filing={filing}
        region={region}
        corporationId={corporationId}
        companyId={companyId}
      />
    </Tabs>
  );
};

export { IndirectTaxFilingWorkflowDetailPage };
